// src/hooks/useList/useListAdapter.ts
import { useMemo } from "react";
import qs from "qs";
import { Services } from "$utils/typeConstants";
import { UseListOptions } from "./types";
import { useList } from ".";

const EMPTY_ADAPTER_LIST: never[] = [];

// Extend the UseListOptions type to include cacheKey
type ExtendedUseListOptions = UseListOptions & {
  cacheKey?: string;
};

/**
 * This adapter makes it easier to migrate from the old useList hook to the new one
 * by providing a similar API to the old hook while using the new implementation internally.
 */
export function useListAdapter<
  T extends {
    id: string | number;
    [key: string]: any;
  },
>(
  endpoint: string,
  options: Partial<
    UseListOptions & { cachePrefix?: string; cacheKey?: string }
  > = {},
  service: Services = Services.MAIN_API
) {
  // Map old options format to new options format
  const newOptions = {
    service,
    defaultFilters: options.defaultFilters || {},
    defaultSort:
      typeof options.defaultSort === "string"
        ? {
            field: options.defaultSort.split(":")[0],
            direction: options.defaultSort.split(":")[1] as "asc" | "desc",
          }
        : options.defaultSort,
    defaultPageSize: options.defaultPageSize || 20,
    populate: options.populate,
    fields: options.fields,
    additionalParams: options.additionalParams,
    ignoreSideBarFilter: options.ignoreSideBarFilter,
    enabled: options.enabled ?? true,
    jwt: options.jwt || localStorage.getItem("access_token"), // Pass through the JWT token
    cacheKey: options.cacheKey || options.cachePrefix,
  } as ExtendedUseListOptions;

  // Use the new hook
  const {
    data: rawData,
    filters,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    error,
    setFilter,
    setFilters,
    pagination,
    refetch,
    removeItem,
    addItem,
    setData,
  } = useList<T>(endpoint, newOptions);

  // Transform Strapi V4 data format if needed.
  // Keep a stable empty array when rawData is empty — a fresh `[]` each render
  // re-triggers consumers' effects (receptions list hit Maximum update depth in dev).
  const items = useMemo(() => {
    if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
      return EMPTY_ADAPTER_LIST;
    }
    if (!Array.isArray(rawData)) {
      // If the data is not an array, return it as is
      return rawData;
    }
    return rawData.map((item) => {
      // If the data is already in the expected format, return it as is
      if (!item.attributes) return item;
      // Transform Strapi V4 format to the format expected by components
      return {
        id: item.id,
        ...item.attributes,
        // Expand nested relations if needed
        ...(item.attributes.receptions && {
          receptions: item.attributes.receptions.data || [],
        }),
        ...(item.attributes.files && {
          files: item.attributes.files.data || null,
        }),
      };
    });
  }, [rawData]);

  // Create a function that mimics the old getParamsAsStrapiString
  const getParamsAsStrapiString = (
    withPagination = true,
    paginationSize = pagination.pageSize,
    paginationIndex = pagination.pageIndex
  ) => {
    const query: any = {
      filters,
      sort: newOptions.defaultSort ? [newOptions.defaultSort] : [],
      populate: newOptions.populate,
    };
    if (withPagination) {
      query.pagination = {
        page: paginationIndex + 1,
        pageSize: paginationSize,
      };
    }

    return qs.stringify(query, { encodeValuesOnly: true });
  };

  // Return an object that matches the old useList API
  return {
    // Data
    items,
    setItems: setData,
    // Status
    isItemsValid: !isError,
    isPreviousItems: false,
    error: error?.message || null,
    isFetching,
    isLoading,
    isRefetching,
    // Fetch function
    fetchItems: refetch,
    getParamsAsStrapiString,
    // Pagination
    pageCount: pagination.pageCount,
    pageIndex: pagination.pageIndex,
    itemsCount: pagination.total,
    setPageIndex: pagination.setPageIndex,
    itemsPerPage: pagination.pageSize,
    setItemsPerPage: pagination.setPageSize,
    allItemsFetched: pagination.pageIndex >= pagination.pageCount - 1,
    // Filtering and sorting
    filters,
    setFilter,
    setFilters,
    // Item manipulation
    removeListItem: removeItem,
    addListItem: addItem,
    // Additional methods to maintain compatibility
    fetchNextItems: () => pagination.setPageIndex(pagination.pageIndex + 1),
  };
}

export default useListAdapter;
